/**
 * The `/feed` header (FEED_SPEC § Header).
 *
 * The view screen's sticky pill, minus the mode switcher: back chevron + "Feed" on the left, Sort
 * and Date Range on the right — the *same* `SortMenu` / `RangePanel` the view screens use, so a
 * sort means one thing everywhere. Under the drawer breakpoint a Filter trigger joins them, since
 * the sidebar is not on screen to speak for itself.
 *
 * The sub-row is `meta.summaryDisplay`, rendered verbatim (§12.5): the server owns the words and
 * the pluralization, the client owns nothing but the placement.
 */

import { Link } from 'react-router';
import { CalendarIcon, ChevronLeftIcon, FilterIcon, Menu } from '../../components';
import { HOME_PATH } from '../../app/routes';
import { formatDateCompact } from '../../lib/datetime';
import { RangePanel } from '../view/RangePanel';
import { SortMenu } from '../view/SortMenu';
import { RANGE_PRESET_LABEL, type ResolvedRange, type ViewSort } from '../view/viewState';
import type { ViewRangeState } from '@lw/shared';

/** Action-button glyph size — matches the view header's, since it is the same pill. */
const ACTION_ICON = 13;

export type FeedHeaderProps = {
  /** `undefined` while the first page is in flight; the sub-row stays blank rather than guessing. */
  summaryDisplay: string | undefined;
  sort: ViewSort;
  onSortChange: (next: ViewSort) => void;
  range: ViewRangeState;
  resolvedRange: ResolvedRange;
  onRangeChange: (next: ViewRangeState) => void;
  timezone: string;
  now: number;
  /** Only under the drawer breakpoint: the sidebar is unmounted and needs a way in. */
  onOpenFilters: (() => void) | null;
  filterCount: number;
};

export function FeedHeader({
  summaryDisplay,
  sort,
  onSortChange,
  range,
  resolvedRange,
  onRangeChange,
  timezone,
  now,
  onOpenFilters,
  filterCount,
}: FeedHeaderProps) {
  const rangeLabel =
    range.preset === 'custom'
      ? `${formatDateCompact(resolvedRange.from, timezone)} – ${formatDateCompact(resolvedRange.to, timezone)}`
      : RANGE_PRESET_LABEL[range.preset];

  return (
    <>
      <div className="view-header">
        <div className="view-header__pill">
          <div className="view-header__left">
            <Link className="icon-btn" to={HOME_PATH} aria-label="Back to profiles">
              <ChevronLeftIcon />
            </Link>
            <span className="view-header__name">Feed</span>
          </div>

          <div className="view-header__actions">
            {onOpenFilters === null ? null : (
              <button
                type="button"
                className="table-action table-action--icon"
                aria-label="Filters"
                onClick={onOpenFilters}
              >
                <FilterIcon size={ACTION_ICON} />
                {filterCount > 0 ? <span className="count-badge">{filterCount}</span> : null}
              </button>
            )}

            {/* `isGroup` opens the Author sort: the global feed is always multi-author. */}
            <SortMenu sort={sort} onChange={onSortChange} isGroup />

            <Menu
              label={
                <>
                  <CalendarIcon size={ACTION_ICON} />
                  <span className="table-action__label">{rangeLabel}</span>
                </>
              }
              align="right"
              panelClassName="menu__panel--pad"
            >
              {() => (
                <RangePanel
                  range={range}
                  resolved={resolvedRange}
                  onChange={onRangeChange}
                  timezone={timezone}
                  now={now}
                />
              )}
            </Menu>
          </div>
        </div>
      </div>

      <div className="view-sub feed-sub">{summaryDisplay ?? ' '}</div>
    </>
  );
}

export default FeedHeader;
